/**
 * NeuralNavbar.jsx
 * src/components/ui/NeuralNavbar.jsx
 *
 * Layout (desktop): [ ☀/🌙 | divider | logo | Founders | … | Contact ]
 * Layout (mobile):  hamburger → drawer with toggle in the header row
 *
 * Changes vs previous version:
 *  - ThemeToggle is the FIRST element in the desktop dock
 *  - ThemeToggle moves to the drawer header row on mobile (next to close ✕)
 *  - Divider between toggle and logo is theme-aware (cyan dark / amber light)
 *  - Toggle wrapper gets its own glass pill style so it looks part of the dock
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import './NeuralNavbar.css';
import {
  motion as Motion,
  useMotionValue,
  useSpring,
  AnimatePresence,
} from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { SDMonogram } from '../branding/BrandMark';
import ThemeToggle from './ThemeToggle';

/* ─── constants ──────────────────────────────────────────────── */
const SPRING_DRAG = { stiffness: 280, damping: 28, mass: 0.9 };
const SPRING_TILT = { stiffness: 120, damping: 18 };
const EASE        = [0.22, 1, 0.36, 1];
const PULSE_DUR   = [2.8, 3.4, 2.6, 3.1, 2.9, 3.6];
const CURRENT_YEAR = new Date().getFullYear();

/* ─── styles ──────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&display=swap');

  /* ── outer wrapper ──────────────────────────────────────── */
  .nn-outer {
    position: fixed;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 200;
    pointer-events: none;
    width: max-content;
    max-width: calc(100vw - 1rem);
  }

  /* ── toggle pill — wraps ThemeToggle in the dock ────────── */
  .nn-toggle-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border-radius: 999px;
    border: 1px solid rgba(0, 229, 255, 0.22);
    background: linear-gradient(135deg, rgba(10,25,47,.82), rgba(5,15,35,.92));
    backdrop-filter: blur(16px) saturate(1.4);
    flex-shrink: 0;
    pointer-events: all;
    cursor: default;
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }
  .nn-toggle-pill:hover {
    border-color: rgba(0, 229, 255, 0.42);
    box-shadow: 0 0 14px rgba(0, 229, 255, 0.14);
  }

  /* ── dock divider ───────────────────────────────────────── */
  .nn-divider {
    width: 1px;
    height: 18px;
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(0, 229, 255, 0.28) 30%,
      rgba(0, 229, 255, 0.28) 70%,
      transparent
    );
    border-radius: 999px;
    flex-shrink: 0;
    align-self: center;
  }

  /* ── mobile hamburger button ─────────────────────────────── */
  .nn-hamburger {
    display: none;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.6rem 1rem;
    border-radius: 999px;
    border: 1px solid var(--nav-border, rgba(0,229,255,.2));
    background: var(--nav-bg, linear-gradient(135deg,rgba(10,25,47,.88),rgba(5,15,35,.94)));
    backdrop-filter: blur(16px);
    pointer-events: all;
    cursor: pointer;
    color: var(--text-soft, rgba(185,220,240,.8));
    width: 100%;
  }
  .nn-hamburger-logo {
    width: 32px; height: 32px;
    border-radius: 0.6rem;
    overflow: hidden;
    flex-shrink: 0;
  }
  .nn-hamburger-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #00e5ff;
    flex: 1;
  }
  .nn-hamburger-icon {
    color: rgba(0,229,255,.75);
    flex-shrink: 0;
  }

  /* ── mobile drawer ──────────────────────────────────────── */
  .nn-drawer {
    position: fixed;
    inset: 0;
    z-index: 300;
    display: flex;
    flex-direction: column;
    background: var(--nav-bg, linear-gradient(180deg,rgba(5,11,24,.97) 0%,rgba(7,18,38,.99) 100%));
    backdrop-filter: blur(24px);
    padding: 1.5rem;
    overflow-y: auto;
  }

  /* ── drawer header: logo | brand | [spacer] | toggle | close ── */
  .nn-drawer__head {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(0,229,255,.1);
  }
  .nn-drawer__brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
  }
  .nn-drawer__brand-logo {
    width: 40px; height: 40px;
    border-radius: 0.75rem;
    overflow: hidden;
    flex-shrink: 0;
  }
  .nn-drawer__brand-name {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--bioluminescent-cyan, #00e5ff);
  }
  .nn-drawer__toggle {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
  .nn-drawer__close {
    width: 40px; height: 40px;
    border-radius: 999px;
    border: 1px solid rgba(0,229,255,.2);
    background: rgba(0,229,255,.05);
    color: rgba(0,229,255,.8);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
  }

  .nn-drawer__links {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
  }
  .nn-drawer__link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.1rem 1.25rem;
    border-radius: 1rem;
    border: 1px solid rgba(0,229,255,.08);
    background: rgba(0,229,255,.03);
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(185,220,240,.8);
    text-decoration: none;
    transition: all 0.2s ease;
  }
  .nn-drawer__link.is-active {
    border-color: rgba(0,229,255,.35);
    background: rgba(0,229,255,.08);
    color: #00e5ff;
  }
  .nn-drawer__link:active {
    background: rgba(0,229,255,.12);
    color: #00e5ff;
  }

  .nn-drawer__link-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(0,229,255,.4);
    flex-shrink: 0;
  }
  .nn-drawer__link.is-active .nn-drawer__link-dot {
    background: #00e5ff;
    box-shadow: 0 0 6px #00e5ff;
  }
  .nn-drawer__footer {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0,229,255,.08);
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(0,229,255,.28);
    text-align: center;
  }

  /* ── drag track (desktop) ───────────────────────────────── */
  .nn-drag-track {
    position: relative;
    pointer-events: all;
    cursor: grab;
    user-select: none;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 4px;
    padding: 6px;
  }
  .nn-drag-track:active { cursor: grabbing; }

  /* ── SVG connectors ─────────────────────────────────────── */
  .nn-svg {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    pointer-events: none;
    overflow: visible;
  }
  .nn-connector-base { fill:none; stroke:rgba(0,229,255,.1); stroke-width:1; }
  .nn-connector-glow { fill:none; stroke:rgba(0,229,255,.32); stroke-width:1.2; filter:url(#nn-glow); }
  .nn-pulse          { fill:none; stroke-width:2.5; stroke-linecap:round; }

  /* ── nav node ───────────────────────────────────────────── */
  .nn-node {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100px;
    cursor: pointer;
    pointer-events: all;
    flex-shrink: 0;
  }
  .nn-node-glass {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(135deg,rgba(10,25,47,.82),rgba(5,15,35,.92));
    border: 1px solid rgba(0,229,255,.18);
    backdrop-filter: blur(16px) saturate(1.4);
    overflow: hidden;
    transition: border-color .3s;
  }
  .nn-node-glass::before {
    content: '';
    position: absolute;
    top:0; left:-60%; width:40%; height:100%;
    background: linear-gradient(90deg,transparent,rgba(0,229,255,.08),transparent);
    transform: skewX(-18deg);
    animation: nn-shimmer 6s ease-in-out infinite;
  }
  .nn-node-glass::after {
    content: '';
    position: absolute;
    top:0; left:10%; right:10%; height:1px;
    background: linear-gradient(90deg,transparent,rgba(255,255,255,.22),transparent);
  }
  @keyframes nn-shimmer {
    0%,100%{left:-60%;opacity:.4} 50%{left:120%;opacity:.8}
  }
  .nn-node--active .nn-node-glass {
    border-color: rgba(0,229,255,.55);
    background: linear-gradient(135deg,rgba(0,70,100,.72),rgba(0,30,60,.88));
  }
  .nn-label {
    position: relative;
    z-index: 2;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    font-weight: 500;
    letter-spacing: .11em;
    text-transform: uppercase;
    color: rgba(185,220,240,.7);
    white-space: nowrap;
    pointer-events: none;
    transition: color .25s, letter-spacing .25s;
  }
  .nn-node--active .nn-label,
  .nn-node:hover   .nn-label { color:#00e5ff; letter-spacing:.14em; }

  .nn-node-glow {
    position: absolute;
    inset: -4px;
    border-radius: inherit;
    opacity: 0;
    transition: opacity .35s;
    pointer-events: none;
    z-index: -1;
  }
  .nn-node--active .nn-node-glow,
  .nn-node:hover   .nn-node-glow { opacity: 1; }

  /* particles */
  .nn-particles { position:absolute; inset:0; border-radius:inherit; overflow:hidden; pointer-events:none; z-index:1; }
  .nn-particle  {
    position:absolute; border-radius:50%; background:rgba(0,229,255,.55);
    animation: nn-particle-drift linear infinite;
  }
  @keyframes nn-particle-drift {
    0%   { transform:translateY(0) scale(.6); opacity:0; }
    20%  { opacity:.7; }
    80%  { opacity:.4; }
    100% { transform:translateY(-22px) scale(.3); opacity:0; }
  }

  /* ripple */
  .nn-ripple {
    position:absolute; top:50%; left:50%;
    transform:translate(-50%,-50%);
    border-radius:50%;
    border:1.5px solid rgba(0,229,255,.7);
    pointer-events:none; z-index:20;
  }

  /* volume light */
  .nn-volume-light {
    position:absolute; bottom:-22px; left:10%; right:10%; height:18px;
    border-radius:50%;
    background:radial-gradient(ellipse at center,rgba(0,229,255,.18) 0%,transparent 72%);
    filter:blur(6px); pointer-events:none;
    animation:nn-vol-pulse 3s ease-in-out infinite;
  }
  @keyframes nn-vol-pulse {
    0%,100%{opacity:.5;transform:scaleX(.9)} 50%{opacity:1;transform:scaleX(1.05)}
  }

  /* logo node */
  .nn-logo-node { border-radius:50%; overflow:hidden; }
  .nn-logo-node .nn-node-glass {
    border-color:rgba(0,229,255,.28);
    background:linear-gradient(135deg,rgba(8,28,60,.88),rgba(3,10,28,.94));
  }

  /* active dot */
  .nn-active-dot {
    position:absolute; bottom:6px; left:50%;
    transform:translateX(-50%);
    width:3px; height:3px; border-radius:50%;
    background:#00e5ff; box-shadow:0 0 5px #00e5ff; z-index:3;
  }

  /* grab hint */
  .nn-grab-hint {
    position:absolute; bottom:-18px; left:50%; transform:translateX(-50%);
    font-family:'JetBrains Mono',monospace; font-size:7px;
    color:rgba(0,229,255,.22); letter-spacing:.18em; text-transform:uppercase;
    white-space:nowrap; pointer-events:none; opacity:0; transition:opacity .4s;
  }
  .nn-drag-track:hover .nn-grab-hint { opacity:1; }

  /* ── Light mode overrides ───────────────────────────────── */
  [data-theme="light"] .nn-toggle-pill {
    border-color: rgba(180, 100, 10, 0.28);
    background: linear-gradient(135deg, rgba(253,245,225,.88), rgba(248,235,195,.92));
    backdrop-filter: blur(16px) saturate(1.3);
  }
  [data-theme="light"] .nn-toggle-pill:hover {
    border-color: rgba(217, 119, 6, 0.55);
    box-shadow: 0 0 14px rgba(217, 119, 6, 0.18);
  }
  [data-theme="light"] .nn-divider {
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(180, 100, 10, 0.30) 30%,
      rgba(180, 100, 10, 0.30) 70%,
      transparent
    );
  }
  [data-theme="light"] .nn-hamburger {
    background: linear-gradient(135deg, rgba(253,245,225,.92), rgba(248,235,195,.96));
    border-color: rgba(180, 100, 10, 0.28);
  }
  [data-theme="light"] .nn-hamburger-label { color: #b45309; }
  [data-theme="light"] .nn-hamburger-icon  { color: rgba(180,100,10,.75); }
  [data-theme="light"] .nn-drawer {
    background: linear-gradient(180deg, rgba(253,245,225,.97) 0%, rgba(248,235,195,.99) 100%);
  }
  [data-theme="light"] .nn-drawer__head {
    border-bottom-color: rgba(180,100,10,.15);
  }
  [data-theme="light"] .nn-drawer__brand-name { color: #b45309; }
  [data-theme="light"] .nn-drawer__close {
    border-color: rgba(180,100,10,.25);
    background: rgba(180,100,10,.06);
    color: rgba(180,100,10,.8);
  }
  [data-theme="light"] .nn-drawer__link {
    border-color: rgba(180,100,10,.10);
    background: rgba(180,100,10,.04);
    color: rgba(80,40,10,.75);
  }
  [data-theme="light"] .nn-drawer__link.is-active {
    border-color: rgba(180,100,10,.40);
    background: rgba(180,100,10,.10);
    color: #b45309;
  }
  [data-theme="light"] .nn-drawer__link:active {
    background: rgba(180,100,10,.14);
    color: #b45309;
  }
  [data-theme="light"] .nn-drawer__link-dot { background: rgba(180,100,10,.45); }
  [data-theme="light"] .nn-drawer__link.is-active .nn-drawer__link-dot {
    background: #b45309;
    box-shadow: 0 0 6px rgba(180,100,10,.6);
  }
  [data-theme="light"] .nn-drawer__footer { color: rgba(180,100,10,.32); }
  [data-theme="light"] .nn-node-glass {
    background: linear-gradient(135deg, rgba(253,245,225,.82), rgba(248,235,195,.88));
    border-color: rgba(180,100,10,.22);
  }
  [data-theme="light"] .nn-node--active .nn-node-glass {
    border-color: rgba(180,100,10,.55);
    background: linear-gradient(135deg, rgba(255,240,200,.82), rgba(248,225,160,.88));
  }
  [data-theme="light"] .nn-label { color: rgba(80,40,10,.7); }
  [data-theme="light"] .nn-node--active .nn-label,
  [data-theme="light"] .nn-node:hover .nn-label { color: #b45309; }
  [data-theme="light"] .nn-connector-base { stroke: rgba(180,100,10,.12); }
  [data-theme="light"] .nn-connector-glow { stroke: rgba(180,100,10,.35); }
  [data-theme="light"] .nn-particle { background: rgba(180,100,10,.55); }
  [data-theme="light"] .nn-active-dot {
    background: #b45309;
    box-shadow: 0 0 5px rgba(180,100,10,.8);
  }
  [data-theme="light"] .nn-logo-node .nn-node-glass {
    border-color: rgba(180,100,10,.32);
    background: linear-gradient(135deg, rgba(253,240,210,.90), rgba(248,225,165,.92));
  }
  [data-theme="light"] .nn-volume-light {
    background: radial-gradient(ellipse at center, rgba(180,100,10,.18) 0%, transparent 72%);
  }
  [data-theme="light"] .nn-grab-hint { color: rgba(180,100,10,.25); }

  /* ── RESPONSIVE ─────────────────────────────────────────── */
  @media (max-width: 767px) {
    .nn-outer {
      top: 12px;
      left: 1rem;
      right: 1rem;
      transform: none;
      width: auto;
      max-width: none;
    }
    .nn-hamburger  { display: flex; }
    .nn-drag-track { display: none; }
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    .nn-outer { top: 14px; }
    .nn-hamburger { display: none; }
    .nn-drag-track { gap: 4px !important; padding: 5px 6px !important; }
    .nn-label { font-size: 9px !important; letter-spacing: 0.08em !important; }
    .nn-node  { padding: 7px 10px !important; }
    .nn-logo-node { width: 36px !important; height: 36px !important; }
    .nn-grab-hint { display: none; }
    .nn-toggle-pill { padding: 4px !important; }
  }

  @media (min-width: 1024px) {
    .nn-hamburger { display: none; }
    .nn-drag-track { display: flex; }
  }

  @media (min-width: 1536px) {
    .nn-outer { top: 24px; }
    .nn-node  { padding: 10px 20px !important; }
    .nn-label { font-size: 10px !important; letter-spacing: 0.13em !important; }
    .nn-logo-node { width: 58px !important; height: 58px !important; }
  }

  /* ── sliding underline ──────────────────────────────────── */
  .nn-underline {
    position: absolute;
    bottom: -3px;
    left: 10%;
    right: 10%;
    height: 2px;
    background: linear-gradient(90deg, #97f6ff, #61dfff);
    border-radius: 1px;
    box-shadow: 0 0 8px rgba(151, 246, 255, 0.55), 0 0 2px rgba(151, 246, 255, 0.9);
    pointer-events: none;
    z-index: 4;
  }
  [data-theme="light"] .nn-underline {
    background: linear-gradient(90deg, #b45309, #d97706);
    box-shadow: 0 0 8px rgba(180, 83, 9, 0.45), 0 0 2px rgba(180, 83, 9, 0.8);
  }
`;

/* ─── scroll spy ──────────────────────────────────────────────── */
function useActiveSection(navigation) {
  const [activeHref, setActiveHref] = useState('#hero');

  useEffect(() => {
    const hrefs = ['#hero', ...navigation.map((n) => n.href)];
    const ratioMap = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => ratioMap.set(e.target.id, e.intersectionRatio));
        for (const href of hrefs) {
          const id = href.replace('#', '');
          if ((ratioMap.get(id) ?? 0) > 0) { setActiveHref(href); break; }
        }
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 },
    );

    hrefs.forEach((href) => {
      const el = document.getElementById(href.replace('#', ''));
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [navigation]);

  return activeHref;
}

/* ─── particles ───────────────────────────────────────────────── */
const PARTICLES = Array.from({ length: 4 }, (_, i) => ({
  id: i, left: 15 + i * 18,
  size: 1.5 + (i % 2), duration: 2.2 + i * 0.4, delay: i * 0.55,
}));

function NodeParticles({ active }) {
  if (!active) return null;
  return (
    <div className="nn-particles">
      {PARTICLES.map((p) => (
        <span key={p.id} className="nn-particle" style={{
          left: `${p.left}%`, bottom: '4px',
          width: `${p.size}px`, height: `${p.size}px`,
          animationDuration: `${p.duration}s`,
          animationDelay: `${p.delay}s`,
        }} />
      ))}
    </div>
  );
}

function RadarRipple() {
  return (
    <AnimatePresence>
      {[0, 1, 2].map((i) => (
        <Motion.div key={i} className="nn-ripple"
          initial={{ width: 8, height: 8, opacity: .9 }}
          animate={{ width: 80, height: 80, opacity: 0 }}
          exit={{}}
          transition={{ duration: .9, delay: i * .18, ease: 'easeOut' }}
        />
      ))}
    </AnimatePresence>
  );
}

/* ─── SVG connectors ──────────────────────────────────────────── */
function NeuralConnectors({ nodeRefs, containerWidth, containerHeight, hoveredIdx, activeIdx }) {
  const [paths, setPaths] = useState([]);

  useEffect(() => {
    if (!nodeRefs.current?.length) return;
    const container = nodeRefs.current[0]?.closest('.nn-drag-track');
    if (!container) return;
    const cr = container.getBoundingClientRect();

    const pts = nodeRefs.current.map((ref) => {
      if (!ref) return null;
      const r = ref.getBoundingClientRect();
      return { cx: r.left - cr.left + r.width / 2, cy: r.top - cr.top + r.height / 2 };
    }).filter(Boolean);

    if (pts.length < 2) return;
    const built = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const mx = (a.cx + b.cx) / 2;
      const my = (a.cy + b.cy) / 2;
      const d = `M ${a.cx} ${a.cy} Q ${mx} ${my} ${b.cx} ${b.cy}`;
      const dx = b.cx - a.cx, dy = b.cy - a.cy;
      const len = Math.sqrt(dx * dx + dy * dy) * 1.1;
      built.push({ d, len, toIdx: i + 1 });
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPaths(built);
  }, [nodeRefs, containerWidth, containerHeight]);

  return (
    <svg className="nn-svg" aria-hidden="true">
      <defs>
        <filter id="nn-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="nn-pulse-glow">
          <feGaussianBlur stdDeviation="1.8" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="nn-pulse-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#00e5ff" stopOpacity="0" />
          <stop offset="50%"  stopColor="#00e5ff" stopOpacity="1" />
          <stop offset="100%" stopColor="#7c5cff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {paths.map((p, i) => {
        const isActive = hoveredIdx === p.toIdx || activeIdx === p.toIdx;
        const pulseLen = p.len * .18;
        return (
          <g key={i}>
            <path d={p.d} className="nn-connector-base" />
            <path d={p.d} className="nn-connector-glow" style={{ opacity: isActive ? 1 : 0.4 }} />
            <path d={p.d} className="nn-pulse"
              stroke="url(#nn-pulse-grad)" filter="url(#nn-pulse-glow)"
              strokeDasharray={`${pulseLen} ${p.len}`}
              strokeDashoffset={p.len}
              style={{ opacity: isActive ? 1 : 0.55 }}
            >
              <animate attributeName="stroke-dashoffset"
                from={p.len + pulseLen} to={-pulseLen}
                dur={`${PULSE_DUR[i % PULSE_DUR.length]}s`}
                repeatCount="indefinite" />
            </path>
          </g>
        );
      })}
    </svg>
  );
}

/* ─── single nav node ─────────────────────────────────────────── */
function NavNode({ label, href, isActive, isLogo, onClick, nodeRef, index, onHover }) {
  const [rippling, setRippling] = useState(false);
  const [hovered, setHovered]   = useState(false);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);

  const handleMouseEnter = () => { setHovered(true); onHover(index); };
  const handleMouseLeave = () => {
    setHovered(false); onHover(-1);
    rotateX.set(0); rotateY.set(0);
  };
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    rotateX.set(((e.clientY - rect.top  - rect.height / 2) / rect.height) * -14);
    rotateY.set(((e.clientX - rect.left - rect.width  / 2) / rect.width)  *  14);
  };
  const handleClick = (e) => {
    e.preventDefault();
    setRippling(true);
    setTimeout(() => setRippling(false), 1000);
    onClick?.(href);
  };

  if (isLogo) return (
    <Motion.a ref={nodeRef} href="#hero" className="nn-node nn-logo-node"
      style={{ width: 44, height: 44, rotateX, rotateY, perspective: 400 }}
      onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove} onClick={handleClick} aria-label="Back to top"
    >
      <div className="nn-node-glass" />
      <NodeParticles active={hovered || isActive} />
      <SDMonogram compact framed={false} rings={false}
        className="relative z-10" style={{ width: 30, height: 30 }} />
      <Motion.div className="nn-node-glow"
        style={{ background:'radial-gradient(circle,rgba(0,229,255,.28) 0%,transparent 70%)',
          boxShadow:'0 0 22px 4px rgba(0,229,255,.18)' }} />
      {rippling && <RadarRipple />}
    </Motion.a>
  );

  return (
    <Motion.a ref={nodeRef} href={href}
      className={`nn-node ${isActive ? 'nn-node--active' : ''}`}
      style={{ rotateX, rotateY, padding: '8px 14px', perspective: 400 }}
      onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove} onClick={handleClick}
      aria-current={isActive ? 'page' : undefined}
    >
      <div className="nn-node-glass" />
      <NodeParticles active={hovered || isActive} />
      <span className="nn-label">{label}</span>
      <Motion.div className="nn-node-glow"
        style={{ background:'radial-gradient(ellipse,rgba(0,229,255,.2) 0%,transparent 72%)',
          boxShadow: isActive
            ? '0 0 28px 6px rgba(0,229,255,.22),inset 0 0 12px rgba(0,229,255,.08)'
            : '0 0 18px 4px rgba(0,229,255,.14)' }} />
      {isActive && (
        <Motion.div className="nn-active-dot" layoutId="nn-active-dot"
          transition={{ type:'spring', stiffness:380, damping:26 }} />
      )}
      {isActive && !isLogo && (
        <Motion.div
          className="nn-underline"
          layoutId="nav-underline"
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        />
      )}
      {rippling && <RadarRipple />}
    </Motion.a>
  );
}

/* ─── mobile drawer ───────────────────────────────────────────── */
function MobileDrawer({ navigation, activeHref, onClose, onNavigate }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <Motion.div className="nn-drawer"
      initial={{ opacity: 0, x: '100%' }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: '100%' }}
      transition={{ duration: 0.35, ease: EASE }}
    >
      {/* Header: logo | brand name | ★ theme toggle ★ | close */}
      <div className="nn-drawer__head">
        <div className="nn-drawer__brand">
          <div className="nn-drawer__brand-logo">
            <SDMonogram compact framed={false} rings={false}
              style={{ width: '100%', height: '100%' }} />
          </div>
          <span className="nn-drawer__brand-name">Sanjivani DevWorks</span>
        </div>

        {/* ★ ThemeToggle — top-right of drawer header ★ */}
        <div className="nn-drawer__toggle" aria-label="Toggle theme">
          <ThemeToggle />
        </div>

        <button className="nn-drawer__close" onClick={onClose} aria-label="Close menu">
          <X size={18} />
        </button>
      </div>

      {/* Nav links */}
      <nav className="nn-drawer__links">
        {navigation.map((item, i) => (
          <Motion.a
            key={item.href}
            href={item.href}
            className={`nn-drawer__link ${item.href === activeHref ? 'is-active' : ''}`}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: i * 0.06, ease: EASE }}
            onClick={(e) => { e.preventDefault(); onNavigate(item.href); onClose(); }}
          >
            {item.label}
            <span className="nn-drawer__link-dot" />
          </Motion.a>
        ))}
      </nav>

      {/* Footer — copyright only, toggle moved to header */}
      <div className="nn-drawer__footer">
        Sanjivani DevWorks © {CURRENT_YEAR}
      </div>
    </Motion.div>
  );
}

/* ─── main export ─────────────────────────────────────────────── */
export default function NeuralNavbar({ navigation = [], navScale, navY, activeScene = 'hero' }) {
  if (activeScene === 'loader') return null;
  return <NeuralNavbarInner navigation={navigation} navScale={navScale} navY={navY} />;
}

/* Inner — hooks only run after loader exits */
function NeuralNavbarInner({ navigation, navScale, navY }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(-1);
  const [dims, setDims]             = useState({ w: 0, h: 0 });

  const nodeRefs = useRef([]);
  const trackRef = useRef(null);
  const dragX    = useMotionValue(0);
  const springX  = useSpring(dragX, SPRING_DRAG);
  const dockRotX = useSpring(0, SPRING_TILT);
  const dockRotY = useSpring(0, SPRING_TILT);

  const activeHref = useActiveSection(navigation);

  /* dock 3-D tilt on mouse */
  const onDockMouseMove = useCallback((e) => {
    dockRotX.set(((e.clientY - window.innerHeight / 2) / window.innerHeight) * -4);
    dockRotY.set(((e.clientX - window.innerWidth  / 2) / window.innerWidth)  *  5);
  }, [dockRotX, dockRotY]);

  useEffect(() => {
    window.addEventListener('mousemove', onDockMouseMove);
    return () => window.removeEventListener('mousemove', onDockMouseMove);
  }, [onDockMouseMove]);

  /* resize observer for connector geometry */
  useEffect(() => {
    if (!trackRef.current) return;
    const obs = new ResizeObserver(() => {
      const r = trackRef.current?.getBoundingClientRect();
      if (r) setDims({ w: r.width, h: r.height });
    });
    obs.observe(trackRef.current);
    return () => obs.disconnect();
  }, []);

  const nodes = [
    { id: 'logo', isLogo: true, href: '#hero', label: '' },
    ...navigation.map((item) => ({ id: item.href, isLogo: false, ...item })),
  ];
  const activeIdx = nodes.findIndex((n) => n.href === activeHref);
  const maxDragOffset = typeof window === 'undefined'
    ? 20
    : Math.max(0, Math.min(20, Math.floor(((window.innerWidth - dims.w) / 2) - 12)));

  const handleNavigate = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Motion.div
        className="nn-outer"
        style={{ scale: navScale, y: navY, rotateX: dockRotX, rotateY: dockRotY, perspective: 800 }}
        initial={{ opacity: 0, y: -32, filter: 'blur(12px)' }}
        animate={{ opacity: 1, y: 0,   filter: 'blur(0px)'  }}
        transition={{ duration: 1.1, delay: 0.12, ease: EASE }}
        onMouseLeave={() => { dockRotX.set(0); dockRotY.set(0); }}
      >
        {/* ── MOBILE: hamburger ── */}
        <button
          className="nn-hamburger"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          style={{ pointerEvents: 'all' }}
        >
          <div className="nn-hamburger-logo">
            <SDMonogram compact framed={false} rings={false}
              style={{ width: '100%', height: '100%' }} />
          </div>
          <span className="nn-hamburger-label">Sanjivani DevWorks</span>
          <Menu size={18} className="nn-hamburger-icon" />
        </button>

        {/* ── DESKTOP / TABLET: floating dock ── */}
        <Motion.div
          ref={trackRef}
          className="nn-drag-track"
          drag="x"
          dragConstraints={{ left: -maxDragOffset, right: maxDragOffset }}
          dragElastic={0.12}
          dragMomentum={false}
          dragTransition={{ bounceStiffness: 260, bounceDamping: 22 }}
          style={{ x: springX }}
        >
          <NeuralConnectors
            nodeRefs={nodeRefs}
            containerWidth={dims.w}
            containerHeight={dims.h}
            hoveredIdx={hoveredIdx}
            activeIdx={activeIdx}
          />

          {/* ★ ThemeToggle — FIRST item in dock ★ */}
          <div className="nn-toggle-pill" role="group" aria-label="Theme toggle">
            <ThemeToggle />
          </div>

          {/* Divider separating toggle from nav nodes */}
          <div className="nn-divider" aria-hidden="true" />

          {/* Logo node + nav links */}
          {nodes.map((node, i) => (
            <NavNode
              key={node.id}
              label={node.label}
              href={node.href}
              isActive={i === activeIdx}
              isLogo={node.isLogo}
              index={i}
              onHover={setHoveredIdx}
              onClick={handleNavigate}
              nodeRef={(el) => { nodeRefs.current[i] = el; }}
            />
          ))}

          <div className="nn-volume-light" />
          <span className="nn-grab-hint">drag</span>
        </Motion.div>
      </Motion.div>

      {/* ── MOBILE DRAWER ── */}
      <AnimatePresence>
        {drawerOpen && (
          <MobileDrawer
            navigation={navigation}
            activeHref={activeHref}
            onClose={() => setDrawerOpen(false)}
            onNavigate={handleNavigate}
          />
        )}
      </AnimatePresence>
    </>
  );
}
